import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Grid2X2, MapPin, ChevronRight, Trash2, Rows3, Layers, Pencil, Copy, GripVertical, ChevronUp, ChevronDown, Bookmark, RefreshCw } from "lucide-react";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Optional per-row color accents (rich modern shades). Empty value = no color.
const ROW_COLORS = [
  { name: "None", value: "" },
  { name: "Amber", value: "#F59E0B" },
  { name: "Orange", value: "#F97316" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Green", value: "#10B981" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Pink", value: "#EC4899" },
];

// Built-in starting points the operator can load into the builder, then rename
// and tweak freely before saving as their own template.
const PRESETS = {
  "sandstar-ark": {
    label: "Sandstar Ark — 6 shelves / 42 spots",
    name: "Sandstar Ark",
    // Shelf sizes from the Vendco "Standard Machine Setup Guide" for Sandstar combo machines:
    // 3 snack shelves (5 x ~12, 8 x ~20, 5 x ~12) over 3 drink shelves (8 x ~7 each).
    description: "Snacks on top, drinks below: sports/tea, soda/water, then energy or soda.",
    rows: [
      { slots: 5, depth: 12, label: "Snacks", color: "#F59E0B" },
      { slots: 8, depth: 20, label: "Candy / Cookies", color: "#EC4899" },
      { slots: 5, depth: 12, label: "Chips", color: "#F59E0B" },
      { slots: 8, depth: 7, label: "Sports / Tea", color: "#14B8A6" },
      { slots: 8, depth: 7, label: "Beverages", color: "#3B82F6" },
      { slots: 8, depth: 7, label: "Energy / Soda", color: "#8B5CF6" },
    ],
  },
  "sandstar-ark-freezer": {
    label: "Sandstar Ark freezer — 6 shelves / 35 spots",
    name: "Sandstar Ark Freezer",
    // Estimated shelf sizes (not from a spec sheet): measure the first freezer and adjust.
    description: "Ice cream bars and novelties on top, pints, then microwave meals and breakfast.",
    rows: [
      { slots: 6, depth: 8, label: "Ice cream bars", color: "#EC4899" },
      { slots: 6, depth: 8, label: "Novelties", color: "#8B5CF6" },
      { slots: 5, depth: 5, label: "Pints / Uncrustables", color: "#3B82F6" },
      { slots: 6, depth: 6, label: "Hot Pockets / Burritos", color: "#F97316" },
      { slots: 6, depth: 5, label: "Meals", color: "#F59E0B" },
      { slots: 6, depth: 6, label: "Breakfast", color: "#10B981" },
    ],
  },
  "uniform-4x6": {
    label: "Uniform grid — 4 rows × 6",
    name: "Drink 4x6",
    description: "",
    rows: Array.from({ length: 4 }, () => ({ slots: 6, depth: 10, label: "", color: "" })),
  },
};

// "Account · Location", or just one when the location is named after the account.
const siteLabel = (site) => [...new Set([site.account_name, site.location_name].filter(Boolean))].join(" · ");

const blankRows = () => [
  { slots: 6, depth: 10, label: "", color: "" },
  { slots: 6, depth: 10, label: "", color: "" },
];

export default function Machines() {
  const { isAdmin } = useAuth();
  const [machines, setMachines] = useState([]);
  const [models, setModels] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dragRow, setDragRow] = useState(null);
  const [mForm, setMForm] = useState({ name: "", model_id: "", location: "", zip_code: "" });
  const [dupOpen, setDupOpen] = useState(false);
  const [dupForm, setDupForm] = useState({ source_id: "", name: "", location: "", zip_code: "" });
  const [starter, setStarter] = useState(null);
  const [unplanned, setUnplanned] = useState([]);
  const [ssBusy, setSsBusy] = useState(false);
  const syncSandstar = async () => {
    setSsBusy(true);
    try {
      const { data } = await api.post("/sandstar/machines/sync");
      const parts = [`${data.synced.length} synced`];
      if (data.linked_now?.length) parts.push(`${data.linked_now.length} newly linked`);
      if (data.unlinked?.length) parts.push(`${data.unlinked.length} not linked yet (open a machine to link it)`);
      toast.success(`Sandstar stock & sales: ${parts.join(", ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sandstar sync failed"); }
    finally { setSsBusy(false); }
  };
  const [setup, setSetup] = useState(null); // { site, mode: "new" | "link", model_id, machine_id }
  const clearStarter = async () => {
    if (!window.confirm("Turn off the starter layout? New machines will start empty.")) return;
    try { await api.delete("/starter-planogram"); setStarter(null); toast.success("Starter layout cleared"); }
    catch (e) { toast.error("Could not clear starter"); }
  };
  const [modelForm, setModelForm] = useState({ name: "", description: "", rows: blankRows() });

  const load = async () => {
    try {
      const [mc, md] = await Promise.all([api.get("/machines"), api.get("/machine-models")]);
      setMachines(mc.data); setModels(md.data);
      try { const u = await api.get("/machines/unplanned"); setUnplanned(u.data); } catch (_e) { setUnplanned([]); }
      try { const s = await api.get("/settings"); setStarter(s.data?.starter_planogram || null); } catch (_e) { /* non-blocking */ }
    } catch (err) {
      console.error("Failed to load machines:", err);
      if (err?.response?.status !== 401) toast.error("Could not load machines");
    }
  };
  useEffect(() => { load(); }, []);

  const totalSlots = (rows) => rows.reduce((n, r) => n + (Number(r.slots) || 0), 0);

  const openNewTemplate = () => {
    setEditingId(null);
    setModelForm({ name: "", description: "", rows: blankRows() });
    setModelOpen(true);
  };

  const openEditTemplate = (md) => {
    setEditingId(md.id);
    setModelForm({
      name: md.name || "",
      description: md.description || "",
      rows: (md.layout && md.layout.length ? md.layout : [{ slots: md.cols || 6, depth: 10, label: "", color: "" }])
        .map((r) => ({ slots: r.slots, depth: r.depth ?? 10, label: r.label || "", color: r.color || "" })),
    });
    setModelOpen(true);
  };

  const openDuplicateTemplate = (md) => {
    setEditingId(null); // clone => creates a brand new template
    setModelForm({
      name: `${md.name} (copy)`,
      description: md.description || "",
      rows: (md.layout && md.layout.length ? md.layout : [{ slots: md.cols || 6, depth: 10, label: "", color: "" }])
        .map((r) => ({ slots: r.slots, depth: r.depth ?? 10, label: r.label || "", color: r.color || "" })),
    });
    setModelOpen(true);
    toast.success("Duplicated — rename and tweak, then create");
  };

  const moveRow = (i, dir) => setModelForm((f) => {
    const j = i + dir;
    if (j < 0 || j >= f.rows.length) return f;
    const rows = [...f.rows];
    [rows[i], rows[j]] = [rows[j], rows[i]];
    return { ...f, rows };
  });

  const dropRow = (target) => setModelForm((f) => {
    if (dragRow === null || dragRow === target) return f;
    const rows = [...f.rows];
    const [moved] = rows.splice(dragRow, 1);
    rows.splice(target, 0, moved);
    return { ...f, rows };
  });

  const loadPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    setModelForm((f) => ({
      name: f.name || p.name,
      description: p.description,
      rows: p.rows.map((r) => ({ ...r })),
    }));
    toast.success(`Loaded ${p.name} — edit rows or save as your own`);
  };

  const setRow = (i, key, val) => {
    setModelForm((f) => {
      const rows = f.rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
      return { ...f, rows };
    });
  };
  const addRow = () => setModelForm((f) => (f.rows.length >= 26 ? f : { ...f, rows: [...f.rows, { slots: 6, depth: 10, label: "", color: "" }] }));
  const removeRow = (i) => setModelForm((f) => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }));

  const saveModel = async () => {
    if (!modelForm.name.trim()) return toast.error("Template name required");
    const layout = modelForm.rows
      .map((r) => ({ slots: Number(r.slots) || 0, depth: Number(r.depth) || 0, label: (r.label || "").trim(), color: r.color || "" }))
      .filter((r) => r.slots > 0);
    if (!layout.length) return toast.error("Add at least one row with slots");
    const payload = { name: modelForm.name.trim(), description: modelForm.description, layout };
    try {
      if (editingId) {
        await api.put(`/machine-models/${editingId}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/machine-models", payload);
        toast.success("Template created");
      }
      setModelOpen(false);
      setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createMachine = async () => {
    if (!mForm.name || !mForm.model_id) return toast.error("Name and template required");
    try { await api.post("/machines", mForm); toast.success("Machine created"); setMOpen(false); setMForm({ name: "", model_id: "", location: "", zip_code: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openDuplicateMachine = (m) => {
    setDupForm({ source_id: m.id, name: `${m.name} (copy)`, location: "", zip_code: "" });
    setDupOpen(true);
  };
  const duplicateMachine = async () => {
    if (!dupForm.name.trim()) return toast.error("Machine name required");
    try {
      await api.post(`/machines/${dupForm.source_id}/duplicate`, { name: dupForm.name.trim(), location: dupForm.location, zip_code: dupForm.zip_code });
      toast.success("Machine duplicated — planogram copied, counts reset");
      setDupOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unlinked = machines.filter((m) => !m.service_machine_id);
  const openSetup = (site) => setSetup({ site, mode: models.length ? "new" : "link", model_id: models[0]?.id || "", machine_id: "" });
  const saveSetup = async () => {
    const { site, mode, model_id, machine_id } = setup;
    try {
      if (mode === "new") {
        if (!model_id) return toast.error("Choose a template");
        await api.post("/machines", { name: site.name, model_id, location: site.location_name, zip_code: "", service_machine_id: site.service_machine_id });
        toast.success(`Planogram created for ${site.name}`);
      } else {
        if (!machine_id) return toast.error("Choose a machine");
        await api.put(`/machines/${machine_id}/link`, { service_machine_id: site.service_machine_id });
        toast.success(`Linked to ${site.name}`);
      }
      setSetup(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const where = (m) => (m.site ? siteLabel(m.site) : m.location) || "No location";

  const delModel = async (id) => { if (!window.confirm("Delete template?")) return; try { await api.delete(`/machine-models/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  const delMachine = async (id) => { if (!window.confirm("Delete machine?")) return; await api.delete(`/machines/${id}`); toast.success("Deleted"); load(); };

  const modelSlots = (md) => md.slot_ids?.length ?? totalSlots(md.layout || []);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div><h1 className="font-heading text-2xl font-bold">Machines</h1><p className="text-sm text-muted-foreground">Reusable templates and your machine fleet.</p></div>
      </div>
      <Tabs defaultValue="machines">
        <TabsList><TabsTrigger value="machines" data-testid="tab-machines">Machines</TabsTrigger><TabsTrigger value="templates" data-testid="tab-templates">Model templates</TabsTrigger></TabsList>

        <TabsContent value="machines" className="mt-4">
          {isAdmin && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Button data-testid="machine-add-button" onClick={() => setMOpen(true)} disabled={!models.length}><Plus className="mr-2 h-4 w-4" />Add machine</Button>
              <Button variant="outline" data-testid="machines-sandstar-sync" onClick={syncSandstar} disabled={ssBusy} title="Live stock and 30-day sales from your Sandstar account"><RefreshCw className={`mr-2 h-4 w-4 ${ssBusy ? "animate-spin" : ""}`} />{ssBusy ? "Syncing…" : "Sync stock & sales"}</Button>
              {starter?.count > 0 && (
                <div className="flex items-center gap-1.5 rounded-md border bg-primary/5 px-2.5 py-1.5 text-xs text-muted-foreground" data-testid="starter-status">
                  <Bookmark className="h-3.5 w-3.5 text-primary" />
                  Starter layout active ({starter.count} products)
                  <button type="button" className="ml-1 font-medium text-primary underline-offset-2 hover:underline" data-testid="starter-clear-button" onClick={clearStarter}>Clear</button>
                </div>
              )}
            </div>
          )}
          {!models.length && isAdmin && <p className="mb-4 text-sm text-amber-700">Create a model template first.</p>}
          {unplanned.length > 0 && (
            <Card className="mb-4 p-4" data-testid="unplanned-machines">
              <div className="font-heading font-semibold">At your locations, no planogram yet</div>
              <p className="mb-3 text-xs text-muted-foreground">Machines added under Customers &amp; Service. Set up a planogram to stock them here.</p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {unplanned.map((u) => (
                  <div key={u.service_machine_id} className="flex items-center justify-between gap-2 rounded-md border p-3" data-testid={`unplanned-${u.service_machine_id}`}>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{u.name}</div>
                      <div className="flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" />{siteLabel(u) || "No location"}</div>
                    </div>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => openSetup(u)} data-testid={`unplanned-setup-${u.service_machine_id}`}>Set up planogram</Button>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          {machines.length === 0 ? <Card className="p-10 text-center text-muted-foreground">No machines you can access yet.</Card> : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {machines.map((m) => (
                <Card key={m.id} className="p-4" data-testid={`machine-card-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div><div className="font-heading font-semibold">{m.name}</div><div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{where(m)} {m.zip_code && `· ${m.zip_code}`}</div></div>
                    <Badge variant="secondary">{(m.slots || []).length} slots</Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">Template: {m.model_name}</div>
                  <div className="mt-2 text-xs text-muted-foreground">Assigned: {(m.slots||[]).filter(s=>s.product_id).length}/{(m.slots||[]).length} slots</div>
                  <div className="mt-3 flex gap-2">
                    <Link to={`/admin/planner/machines/${m.id}`} className="flex-1"><Button variant="outline" className="w-full" data-testid={`machine-open-${m.id}`}>Open planogram <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                    {isAdmin && <Button variant="ghost" size="icon" data-testid={`machine-duplicate-${m.id}`} title="Duplicate machine" onClick={() => openDuplicateMachine(m)}><Copy className="h-4 w-4 text-muted-foreground" /></Button>}
                    {isAdmin && <Button variant="ghost" size="icon" data-testid={`machine-delete-${m.id}`} title="Delete machine" onClick={() => delMachine(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          {isAdmin && <Button className="mb-4" data-testid="model-add-button" onClick={openNewTemplate}><Plus className="mr-2 h-4 w-4" />New template</Button>}
          {models.length === 0 ? <Card className="p-10 text-center text-muted-foreground">No templates yet.</Card> : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {models.map((md) => (
                <Card key={md.id} className="p-4" data-testid={`model-card-${md.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2"><Grid2X2 className="h-5 w-5 text-primary" /><div className="font-heading font-semibold">{md.name}</div></div>
                    {isAdmin && (
                      <div className="flex items-center">
                        <Button variant="ghost" size="icon" data-testid={`model-duplicate-${md.id}`} title="Duplicate" onClick={() => openDuplicateTemplate(md)}><Copy className="h-4 w-4 text-muted-foreground" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`model-edit-${md.id}`} title="Edit" onClick={() => openEditTemplate(md)}><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`model-delete-${md.id}`} title="Delete" onClick={() => delModel(md.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{md.rows} rows · {modelSlots(md)} slots</div>
                  {(md.layout || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`model-layout-${md.id}`}>
                      {md.layout.map((r, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          {r.color && <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />}
                          {LETTERS[i]}{r.label ? ` ${r.label}` : ""}: {r.slots}×{r.depth}
                        </span>
                      ))}
                    </div>
                  )}
                  {md.description && <p className="mt-2 text-xs text-muted-foreground">{md.description}</p>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* --------- Template builder --------- */}
      <Dialog open={modelOpen} onOpenChange={(o) => { setModelOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent data-testid="model-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit template" : "New machine template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr_auto] items-end gap-3">
              <div><Label>Template name</Label><Input data-testid="model-name-input" value={modelForm.name} onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })} placeholder="e.g. Sandstar Ark" /></div>
              <div>
                <Label className="flex items-center gap-1 text-xs"><Layers className="h-3 w-3" />Load preset</Label>
                <Select onValueChange={loadPreset}>
                  <SelectTrigger className="w-[190px]" data-testid="model-preset-select"><SelectValue placeholder="Start from a preset" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRESETS).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <Label className="flex items-center gap-1"><Rows3 className="h-4 w-4" />Rows &amp; capacity</Label>
                <span className="text-xs text-muted-foreground" data-testid="model-total-slots">{modelForm.rows.length} rows · {totalSlots(modelForm.rows)} slots</span>
              </div>
              <div className="grid grid-cols-[1.25rem_1.25rem_1.5rem_1fr_3.5rem_3.5rem_5.5rem] items-center gap-2 px-1 pb-1 text-[11px] font-medium text-muted-foreground">
                <span /><span>Row</span><span /><span>Name (optional)</span><span>Slots</span><span>Cap</span><span className="text-center">Order</span>
              </div>
              <div className="max-h-[42vh] space-y-2 overflow-y-auto pr-1">
                {modelForm.rows.map((r, i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-[1.25rem_1.25rem_1.5rem_1fr_3.5rem_3.5rem_5.5rem] items-center gap-2 rounded-md ${dragRow === i ? "opacity-50" : ""}`}
                    data-testid={`model-row-${i}`}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={() => dropRow(i)}
                  >
                    <span
                      draggable
                      data-testid={`model-row-grip-${i}`}
                      onDragStart={() => setDragRow(i)}
                      onDragEnd={() => setDragRow(null)}
                      className="flex cursor-grab justify-center text-muted-foreground active:cursor-grabbing"
                      title="Drag to reorder"
                    ><GripVertical className="h-4 w-4" /></span>
                    <span className="font-mono text-sm font-semibold text-primary">{LETTERS[i]}</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`model-row-color-${i}`}
                          title="Row color"
                          className="h-5 w-5 rounded-full border border-border"
                          style={{ background: r.color || "transparent" }}
                        >{r.color ? "" : <span className="text-[10px] text-muted-foreground">×</span>}</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2" align="start">
                        <div className="flex max-w-[168px] flex-wrap gap-1.5">
                          {ROW_COLORS.map((c) => (
                            <button
                              key={c.value || "none"}
                              type="button"
                              title={c.name}
                              data-testid={`model-row-color-${i}-${c.value ? c.value.replace('#', '') : 'none'}`}
                              onClick={() => setRow(i, "color", c.value)}
                              className={`flex h-6 w-6 items-center justify-center rounded-full border ${r.color === c.value ? "ring-2 ring-primary ring-offset-1" : "border-border"}`}
                              style={{ background: c.value || "transparent" }}
                            >{c.value ? "" : <span className="text-[10px] text-muted-foreground">×</span>}</button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Input value={r.label} data-testid={`model-row-label-${i}`} placeholder="e.g. Snacks" onChange={(e) => setRow(i, "label", e.target.value)} />
                    <Input type="number" min="1" max="26" value={r.slots} data-testid={`model-row-slots-${i}`} onChange={(e) => setRow(i, "slots", e.target.value)} />
                    <Input type="number" min="0" value={r.depth} data-testid={`model-row-depth-${i}`} onChange={(e) => setRow(i, "depth", e.target.value)} />
                    <div className="flex items-center justify-center">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`model-row-up-${i}`} disabled={i === 0} onClick={() => moveRow(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`model-row-down-${i}`} disabled={i === modelForm.rows.length - 1} onClick={() => moveRow(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`model-row-remove-${i}`} disabled={modelForm.rows.length <= 1} onClick={() => removeRow(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-2 w-full" data-testid="model-add-row" onClick={addRow} disabled={modelForm.rows.length >= 26}><Plus className="mr-1 h-4 w-4" />Add row</Button>
            </div>

            <div><Label>Description</Label><Input data-testid="model-desc-input" value={modelForm.description} onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })} placeholder="Optional notes" /></div>
          </div>
          <DialogFooter><Button data-testid="model-save-button" onClick={saveModel}>{editingId ? "Save changes" : "Create template"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Add machine --------- */}
      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent data-testid="machine-dialog">
          <DialogHeader><DialogTitle>Add machine</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Machine name</Label><Input data-testid="machine-name-input" value={mForm.name} onChange={(e) => setMForm({ ...mForm, name: e.target.value })} placeholder="Lobby Machine A" /></div>
            <div><Label>Model template</Label>
              <Select value={mForm.model_id} onValueChange={(v) => setMForm({ ...mForm, model_id: v })}>
                <SelectTrigger data-testid="machine-model-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>{models.map((md) => <SelectItem key={md.id} value={md.id}>{md.name} · {md.rows} rows / {modelSlots(md)} slots</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input data-testid="machine-location-input" value={mForm.location} onChange={(e) => setMForm({ ...mForm, location: e.target.value })} /></div>
              <div><Label>ZIP code</Label><Input data-testid="machine-zip-input" value={mForm.zip_code} onChange={(e) => setMForm({ ...mForm, zip_code: e.target.value })} placeholder="45202" /></div>
            </div>
            {starter?.count > 0 && (
              <div className="flex items-start gap-2 rounded-lg border bg-primary/5 p-2.5 text-xs" data-testid="starter-note">
                <Bookmark className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="text-muted-foreground">Starts pre-stocked from your <b className="text-foreground">starter layout</b> ({starter.count} products{starter.saved_from ? ` · from ${starter.saved_from}` : ""}). Matching slots get their product; counts start at 0.</span>
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="machine-save-button" onClick={createMachine}>Create machine</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Planogram for a location machine --------- */}
      <Dialog open={!!setup} onOpenChange={(o) => { if (!o) setSetup(null); }}>
        <DialogContent data-testid="setup-dialog">
          <DialogHeader><DialogTitle>Set up planogram: {setup?.site.name}</DialogTitle></DialogHeader>
          {setup && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{siteLabel(setup.site)}</p>
              {unlinked.length > 0 && (
                <Tabs value={setup.mode} onValueChange={(v) => setSetup({ ...setup, mode: v })}>
                  <TabsList><TabsTrigger value="new" disabled={!models.length}>New from template</TabsTrigger><TabsTrigger value="link">Use a machine already here</TabsTrigger></TabsList>
                </Tabs>
              )}
              {setup.mode === "new" ? (
                models.length ? (
                  <div><Label>Model template</Label>
                    <Select value={setup.model_id} onValueChange={(v) => setSetup({ ...setup, model_id: v })}>
                      <SelectTrigger data-testid="setup-model-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
                      <SelectContent>{models.map((md) => <SelectItem key={md.id} value={md.id}>{md.name} · {md.rows} rows / {modelSlots(md)} slots</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : <p className="text-sm text-amber-700">Create a model template first (Model templates tab).</p>
              ) : (
                <div><Label>Planogram machine</Label>
                  <Select value={setup.machine_id} onValueChange={(v) => setSetup({ ...setup, machine_id: v })}>
                    <SelectTrigger data-testid="setup-machine-select"><SelectValue placeholder="Choose machine" /></SelectTrigger>
                    <SelectContent>{unlinked.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.location ? ` · ${m.location}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="setup-save-button" onClick={saveSetup} disabled={setup?.mode === "new" && !models.length}>{setup?.mode === "link" ? "Link machine" : "Create planogram"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Duplicate machine --------- */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent data-testid="dup-machine-dialog">
          <DialogHeader><DialogTitle>Duplicate machine</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copies the planogram (products &amp; capacities) to a new machine. Stock counts start at zero.</p>
          <div className="space-y-3">
            <div><Label>New machine name</Label><Input data-testid="dup-name-input" value={dupForm.name} onChange={(e) => setDupForm({ ...dupForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input data-testid="dup-location-input" value={dupForm.location} onChange={(e) => setDupForm({ ...dupForm, location: e.target.value })} placeholder="New site" /></div>
              <div><Label>ZIP code</Label><Input data-testid="dup-zip-input" value={dupForm.zip_code} onChange={(e) => setDupForm({ ...dupForm, zip_code: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="dup-save-button" onClick={duplicateMachine}>Duplicate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
